import moment from 'moment';

/*
 * The short spoken summary of a trip (read by VoiceSummary).
 * Built only from the trip's own fields; anything missing is skipped.
 * Returns sentences — they are spoken one by one (Chrome cuts off long ones).
 */

const clean = (value) =>
    String(value ?? '')
        .replace(/<[^>]*>/g, ' ') // overview can hold HTML
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const list = (items) => {
    const parts = items.map(clean).filter(Boolean);
    if (parts.length <= 1) return parts[0] || '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

// "₹3,999" reads as "rupees 3,999" on most voices; say it in words instead
const rupees = (n) => `${Math.round(n).toLocaleString('en-IN')} rupees`;

function lowestPrice(trip) {
    const prices = (trip?.price || [])
        .map((p) => (trip?.datesType === 'custom' ? p?.customPrice?.perPerson : p?.price?.actual))
        .map(Number)
        .filter((n) => n > 0);
    return prices.length ? Math.min(...prices) : 0;
}

function lowestReserve(trip) {
    const r = (trip?.price || [])
        .map((p) => Number(trip?.datesType === 'custom' ? p?.customPrice?.reserve : p?.price?.reserve))
        .filter((n) => n > 0);
    return r.length ? Math.min(...r) : 0;
}

// first one or two sentences of the overview, at most ~260 characters
function overviewIntro(overview) {
    const text = clean(overview);
    if (!text) return '';
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    let out = '';
    for (const s of sentences) {
        if ((out + s).length > 260) break;
        out += s;
        if (out.length > 140) break;
    }
    return clean(out || text.slice(0, 260));
}

// next departure date that hasn't passed (moment object) or undefined
function nextDeparture(dates) {
    return (dates || [])
        .map((d) => d?.startDate)
        .filter(Boolean)
        .map((d) => moment(d))
        .filter((d) => d.isValid() && d.isSameOrAfter(moment(), 'day'))
        .sort((a, b) => a - b)[0];
}

/*
 * lang: 'en' | 'kn'. Kannada uses fixed Kannada sentences; what organizers
 * typed (title, place names, stay, food) stays as written. The English
 * overview and day headings are not read in Kannada.
 */
export function tripVoiceScript(trip, dates = [], lang = 'en') {
    if (!trip) return [];
    if (lang === 'kn') return kannadaScript(trip, dates);
    const q = trip.quickView || {};
    const lines = [];

    const length =
        trip.days && trip.nights !== undefined
            ? `${Number(trip.days)}-day, ${Number(trip.nights)}-night`
            : '';
    const kind = Array.isArray(q.type) && q.type.length ? clean(q.type[0]).toLowerCase() : 'trip';
    const where = clean(q.where);
    lines.push(
        `${clean(trip.title)}.` +
            (length || where ? ` ${/^[aeiou8]/i.test(length || kind) ? 'An' : 'A'} ${[length, kind].filter(Boolean).join(' ')}${where ? ` to ${where}` : ''}.` : '')
    );

    const intro = overviewIntro(trip.overview);
    if (intro) lines.push(intro);

    const cities = [...new Set((trip.price || []).map((p) => clean(p?.startCity)).filter(Boolean))];
    if (cities.length) lines.push(`The trip starts from ${list(cities.slice(0, 4))}.`);

    const next = nextDeparture(dates);
    if (next) lines.push(`The next departure is on ${next.format('dddd, D MMMM')}.`);
    if (clean(q.startTime)) lines.push(`It starts at ${clean(q.startTime)}.`);

    const facts = [
        ['Stay', q.stay],
        ['Food', q.food],
        ['Difficulty', q.difficulty],
        ['Trek distance', q.trekDistance]
    ]
        .filter(([, v]) => clean(v))
        .map(([k, v]) => `${k}: ${clean(v)}.`);
    if (facts.length) lines.push(facts.join(' '));

    const days = (trip.itinerary || []).map((d) => clean(d?.dayHeading)).filter(Boolean);
    if (days.length) lines.push(`The plan: ${days.map((h, i) => `day ${i + 1}, ${h}`).join('; ')}.`);

    const price = lowestPrice(trip);
    if (price) lines.push(`The price starts at ${rupees(price)} per person, excluding taxes.`);
    const reserve = lowestReserve(trip);
    if (reserve) lines.push(`You can reserve your seat by paying ${rupees(reserve)} per person now.`);

    lines.push('Please read the full itinerary, inclusions and cancellation policy below before you book.');
    return lines.map((l) => l.replace(/\s+\./g, '.').replace(/\.\.+/g, '.'));
}

/* ---------------------------------------------------------------- Kannada */

const KN_MONTHS = ['ಜನವರಿ', 'ಫೆಬ್ರವರಿ', 'ಮಾರ್ಚ್', 'ಏಪ್ರಿಲ್', 'ಮೇ', 'ಜೂನ್', 'ಜುಲೈ', 'ಆಗಸ್ಟ್', 'ಸೆಪ್ಟೆಂಬರ್', 'ಅಕ್ಟೋಬರ್', 'ನವೆಂಬರ್', 'ಡಿಸೆಂಬರ್'];
const KN_DAYS = ['ಭಾನುವಾರ', 'ಸೋಮವಾರ', 'ಮಂಗಳವಾರ', 'ಬುಧವಾರ', 'ಗುರುವಾರ', 'ಶುಕ್ರವಾರ', 'ಶನಿವಾರ'];
const KN_LEVEL = { easy: 'ಸುಲಭ', moderate: 'ಮಧ್ಯಮ', medium: 'ಮಧ್ಯಮ', difficult: 'ಕಠಿಣ', hard: 'ಕಠಿಣ', challenging: 'ಕಠಿಣ' };

// "6:00 AM" -> "ಬೆಳಿಗ್ಗೆ 6:00"
function knTime(value) {
    const m = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i.exec(clean(value));
    if (!m) return clean(value);
    let h = Number(m[1]);
    const min = m[2] || '00';
    const pm = /pm/i.test(m[3] || '');
    const h24 = m[3] ? (h % 12) + (pm ? 12 : 0) : h;
    const part = h24 < 4 ? 'ರಾತ್ರಿ' : h24 < 12 ? 'ಬೆಳಿಗ್ಗೆ' : h24 < 16 ? 'ಮಧ್ಯಾಹ್ನ' : h24 < 19 ? 'ಸಂಜೆ' : 'ರಾತ್ರಿ';
    h = h24 % 12 || 12;
    return `${part} ${h}:${min}`;
}

const knList = (items) => {
    const parts = items.map(clean).filter(Boolean);
    if (parts.length <= 1) return parts[0] || '';
    return `${parts.slice(0, -1).join(', ')} ಮತ್ತು ${parts[parts.length - 1]}`;
};

const knRupees = (n) => `${Math.round(n).toLocaleString('en-IN')} ರೂಪಾಯಿ`;

function kannadaScript(trip, dates) {
    const q = trip.quickView || {};
    const lines = [];
    const type = Array.isArray(q.type) && q.type.length ? clean(q.type[0]).toLowerCase() : '';
    const kind = /trek|hike/.test(type) ? 'ಚಾರಣ' : 'ಪ್ರವಾಸ';
    const where = clean(q.where);
    const length = trip.days ? `${Number(trip.days)} ದಿನ${trip.nights !== undefined ? ` ${Number(trip.nights)} ರಾತ್ರಿಯ` : ''}` : '';

    lines.push(`${clean(trip.title)}.` + (length || where ? ` ಇದು ${where ? `${where} ಗೆ ` : ''}${length ? `${length} ` : ''}${kind}.` : ''));

    const cities = [...new Set((trip.price || []).map((p) => clean(p?.startCity)).filter(Boolean))];
    if (cities.length) lines.push(`ಈ ${kind} ${knList(cities.slice(0, 4))} ಇಂದ ಆರಂಭವಾಗುತ್ತದೆ.`);

    const next = nextDeparture(dates);
    if (next) lines.push(`ಮುಂದಿನ ಹೊರಡುವ ದಿನಾಂಕ ${KN_DAYS[next.day()]}, ${next.date()} ${KN_MONTHS[next.month()]}.`);
    if (clean(q.startTime)) lines.push(`ಪ್ರಯಾಣ ${knTime(q.startTime)} ಕ್ಕೆ ಆರಂಭವಾಗುತ್ತದೆ.`);

    const level = KN_LEVEL[clean(q.difficulty).toLowerCase()];
    const facts = [
        ['ವಾಸ್ತವ್ಯ', clean(q.stay)],
        ['ಊಟ', clean(q.food)],
        ['ಕಷ್ಟದ ಮಟ್ಟ', level || clean(q.difficulty)],
        ['ಚಾರಣದ ದೂರ', clean(q.trekDistance).replace(/\s*km\b/i, ' ಕಿಲೋಮೀಟರ್')]
    ]
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}: ${v}.`);
    if (facts.length) lines.push(facts.join(' '));

    const days = (trip.itinerary || []).filter((d) => clean(d?.dayHeading)).length;
    if (days) lines.push(`${days} ದಿನಗಳ ಸಂಪೂರ್ಣ ಯೋಜನೆಯನ್ನು ಕೆಳಗೆ ನೀಡಲಾಗಿದೆ.`);

    const price = lowestPrice(trip);
    if (price) lines.push(`ಬೆಲೆ ಒಬ್ಬರಿಗೆ ${knRupees(price)} ಇಂದ ಆರಂಭ, ತೆರಿಗೆ ಹೊರತುಪಡಿಸಿ.`);
    const reserve = lowestReserve(trip);
    if (reserve) lines.push(`ಈಗ ಒಬ್ಬರಿಗೆ ${knRupees(reserve)} ಪಾವತಿಸಿ ನಿಮ್ಮ ಸೀಟನ್ನು ಕಾಯ್ದಿರಿಸಬಹುದು.`);

    lines.push('ಬುಕ್ ಮಾಡುವ ಮೊದಲು ಕೆಳಗಿನ ಸಂಪೂರ್ಣ ವೇಳಾಪಟ್ಟಿ, ಒಳಗೊಂಡಿರುವ ಸೌಲಭ್ಯಗಳು ಮತ್ತು ರದ್ದತಿ ನೀತಿಯನ್ನು ದಯವಿಟ್ಟು ಓದಿ.');
    return lines.map((l) => l.replace(/\s+\./g, '.').replace(/\.\.+/g, '.'));
}

/* ------------------------------------------------- Details tab sections */

const stripEmoji = (s) => s.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}]/gu, '');

// long text -> pieces of at most ~220 characters, cut at sentence ends
// (some browsers stop a single long utterance after ~15 seconds)
function chunks(text, max = 220) {
    const t = stripEmoji(clean(text));
    if (!t) return [];
    const sentences = t.match(/[^.!?]+[.!?]*/g) || [t];
    const out = [];
    let cur = '';
    sentences.forEach((raw) => {
        const s = raw.trim();
        if (!s) return;
        if (s.length > max) {
            if (cur) out.push(cur);
            cur = '';
            // very long sentence: cut at commas
            s.split(/,\s*/).forEach((part) => {
                if ((cur + ', ' + part).length > max && cur) {
                    out.push(cur);
                    cur = part;
                } else cur = cur ? `${cur}, ${part}` : part;
            });
            return;
        }
        if ((cur + ' ' + s).length > max && cur) {
            out.push(cur);
            cur = s;
        } else cur = cur ? `${cur} ${s}` : s;
    });
    if (cur) out.push(cur);
    return out.map((p) => (/[.!?]$/.test(p) ? p : `${p}.`));
}

const listLine = (title, items) => {
    const parts = (items || []).map((i) => stripEmoji(clean(i)).replace(/[.;]+$/, '')).filter(Boolean);
    return parts.length ? chunks(`${title}: ${parts.join('; ')}.`) : [];
};

export const SECTION_LABELS = {
    itinerary: 'Listen to itinerary',
    'inc/exc': 'Listen to inclusions',
    carry: 'Listen to things to carry',
    terms: 'Listen to terms'
};

/**
 * Spoken text for one Details tab (English — organizers write these in English).
 * Returns { lines, groups }: groups[i] = which item line i belongs to
 * (for the itinerary: the day, so it can be highlighted while read).
 */
export function sectionVoiceScript(tabId, trip) {
    const lines = [];
    const groups = [];
    const add = (texts, group = null) =>
        texts.forEach((t) => {
            lines.push(t);
            groups.push(group);
        });

    if (tabId === 'itinerary') {
        (trip?.itinerary || []).forEach((day, i) => {
            // same numbering as the page shows ("Day 0 - …")
            add(chunks(`Day ${i}. ${clean(day?.dayHeading)}.`), i);
            add(chunks(day?.dayDescription), i);
        });
    } else if (tabId === 'inc/exc') {
        add(listLine('Included', trip?.included));
        add(listLine('Not included', trip?.excluded));
    } else if (tabId === 'carry') {
        add(listLine('Things to carry', trip?.carry));
    } else if (tabId === 'terms') {
        const terms = (trip?.terms || []).map((t) => stripEmoji(clean(t))).filter(Boolean);
        if (terms.length) add(['Please listen to the terms of this trip.']);
        terms.forEach((t) => add(chunks(t)));
    }
    return { lines: lines.map((l) => l.replace(/\s+\./g, '.').replace(/\.\.+/g, '.')), groups };
}
